import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader,
  AlertCircle,
  UserPlus,
  UserMinus,
  CheckCircle,
  UserCheck,
  Info,
  ChevronDown,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { eventTeamApiService, EventTeamApiError } from '../../services/eventTeamApi';
import type {
  EventAssignment,
  EventAssignmentRole,
  EventTeamMember,
} from '../../types/eventTeam';
import { EVENT_ASSIGNMENT_ROLES } from '../../types/eventTeam';
import type { EventTeamPerms } from '../../utils/eventTeamPermissions';
import { PlayerProfile } from '../PlayerProfile';
import { CreatorProfile } from '../CreatorProfile';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface EventAssignmentsSectionProps {
  targetType: 'event' | 'project';
  targetId: string;
  perms: EventTeamPerms;
  /**
   * Event-only: route assign/unassign through the `*-with-sync` companions
   * (mirrors roster members onto linked kanban cards — guide §1.5).
   */
  useSync?: boolean;
  refreshNonce?: number;
  onChanged?: () => void;
}

/**
 * Assignment identity is roster-based (guide §7.7): assignments carry `member_id`;
 * we map to uuid/display-name via the roster.
 */
export const EventAssignmentsSection = ({
  targetType,
  targetId,
  perms,
  useSync = false,
  refreshNonce = 0,
  onChanged,
}: EventAssignmentsSectionProps) => {
  const { identity } = useAuth();
  const myUuid = identity?.raw?.custom?.minecraft_uuid;

  const [assignments, setAssignments] = useState<EventAssignment[]>([]);
  const [roster, setRoster] = useState<EventTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notOnRoster, setNotOnRoster] = useState(false);

  // Lead-assign picker
  const [showAssignPicker, setShowAssignPicker] = useState(false);
  const [assignMemberId, setAssignMemberId] = useState('');
  const [assignRole, setAssignRole] = useState<EventAssignmentRole>('helper');
  const [assigning, setAssigning] = useState(false);

  // Self-assign role dropdown
  const [showSelfRoleDropdown, setShowSelfRoleDropdown] = useState(false);
  const [selfBusy, setSelfBusy] = useState(false);

  const membersById = useMemo(() => new Map(roster.map((m) => [m.id, m])), [roster]);
  const membersByUuid = useMemo(() => new Map(roster.map((m) => [m.minecraft_uuid, m])), [roster]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [assignmentsRes, rosterRes] = await Promise.all([
        targetType === 'event'
          ? eventTeamApiService.getEventAssignments(targetId)
          : eventTeamApiService.getProjectAssignments(targetId),
        eventTeamApiService.listTeamMembers({ limit: 200 }),
      ]);
      setAssignments(assignmentsRes);
      setRoster(rosterRes.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  }, [targetId, targetType]);

  useEffect(() => {
    setLoading(true);
    void load();
  }, [load]);

  useEffect(() => {
    if (refreshNonce === 0) return;
    void load();
  }, [refreshNonce, load]);

  const activeAssignments = assignments.filter((a) => a.status === 'active');
  const pastAssignments = assignments.filter((a) => a.status !== 'active');
  const myMember = myUuid ? membersByUuid.get(myUuid) : undefined;
  const myActiveAssignment = myMember
    ? activeAssignments.find((a) => a.member_id === myMember.id)
    : undefined;

  const handleSelfAssign = async (role?: EventAssignmentRole) => {
    if (targetType !== 'event') return;
    setSelfBusy(true);
    setShowSelfRoleDropdown(false);
    try {
      if (useSync && myUuid) {
        await eventTeamApiService.addAssigneeWithSync(targetId, myUuid, role);
      } else {
        await eventTeamApiService.selfAssign(targetId, role);
      }
      setNotOnRoster(false);
      await load();
      onChanged?.();
    } catch (err) {
      if (err instanceof EventTeamApiError && err.status === 404) {
        setNotOnRoster(true);
      } else {
        alert(err instanceof Error ? err.message : 'Failed to self-assign');
      }
    } finally {
      setSelfBusy(false);
    }
  };

  const handleSelfUnassign = async () => {
    if (targetType !== 'event') return;
    setSelfBusy(true);
    try {
      if (useSync && myUuid) {
        await eventTeamApiService.removeAssigneeWithSync(targetId, myUuid);
      } else {
        await eventTeamApiService.selfUnassign(targetId);
      }
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to un-self-assign');
    } finally {
      setSelfBusy(false);
    }
  };

  const handleLeadAssign = async () => {
    if (!assignMemberId) return;
    setAssigning(true);
    try {
      const member = membersById.get(assignMemberId);
      if (targetType === 'event' && useSync && member) {
        await eventTeamApiService.addAssigneeWithSync(targetId, member.minecraft_uuid, assignRole);
      } else if (targetType === 'event') {
        await eventTeamApiService.assignToEvent(targetId, { member_id: assignMemberId, role: assignRole });
      } else {
        await eventTeamApiService.assignToProject(targetId, { member_id: assignMemberId, role: assignRole });
      }
      setShowAssignPicker(false);
      setAssignMemberId('');
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to assign member');
    } finally {
      setAssigning(false);
    }
  };

  const handleRoleChange = async (assignment: EventAssignment, role: EventAssignmentRole) => {
    setBusyId(assignment.id);
    try {
      await eventTeamApiService.updateAssignment(assignment.id, { role });
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update assignment');
    } finally {
      setBusyId(null);
    }
  };

  const handleComplete = async (assignment: EventAssignment) => {
    setBusyId(assignment.id);
    try {
      await eventTeamApiService.completeAssignment(assignment.id);
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to complete assignment');
    } finally {
      setBusyId(null);
    }
  };

  const handleRemove = async (assignment: EventAssignment) => {
    setBusyId(assignment.id);
    try {
      const member = membersById.get(assignment.member_id);
      if (targetType === 'event' && useSync && member) {
        await eventTeamApiService.removeAssigneeWithSync(targetId, member.minecraft_uuid);
      } else {
        await eventTeamApiService.removeAssignment(assignment.id);
      }
      await load();
      onChanged?.();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove assignment');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-2" aria-busy="true" aria-label="Loading assignees">
        <SkeletonLoader className="h-4 w-32" />
        {Array.from({ length: 3 }).map((_, i) => (
          <SkeletonLoader key={i} className="h-11 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <AlertCircle />
        <span className="flex-1">{error}</span>
      </div>
    );
  }

  const renderAssignment = (assignment: EventAssignment) => {
    const member = membersById.get(assignment.member_id);
    const isMine = myMember != null && assignment.member_id === myMember.id;
    const busy = busyId === assignment.id;
    const canRemove = perms.canLead || (isMine && perms.canCreate);
    return (
      <div
        key={assignment.id}
        className={cn(
          'subcard flex items-center justify-between gap-3 px-3 py-2 flex-wrap',
          assignment.status !== 'active' && 'opacity-60',
        )}
      >
        <div className="flex items-center gap-2 min-w-0 flex-wrap">
          {member ? (
            <PlayerProfile uuid={member.minecraft_uuid} name={member.display_name} showUuid={false} inline />
          ) : (
            <span className="text-sm text-glass-300">{assignment.member_id}</span>
          )}
          <span className="badge badge-accent capitalize">{assignment.role}</span>
          {assignment.self_assigned === 1 && (
            <span className="badge badge-purple">self-assigned</span>
          )}
          {assignment.status !== 'active' && (
            <span className="badge badge-neutral capitalize">{assignment.status}</span>
          )}
          <span className="text-[11px] text-glass-500 inline-flex items-center gap-1 tabular-nums">
            assigned {formatDistanceToNow(new Date(assignment.assigned_at), { addSuffix: true })} by{' '}
            <CreatorProfile creatorId={assignment.assigned_by} inline />
          </span>
        </div>

        {assignment.status === 'active' && (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {perms.canLead && (
              <select
                value={assignment.role}
                onChange={(e) => void handleRoleChange(assignment, e.target.value as EventAssignmentRole)}
                disabled={busy}
                className="glass-select text-xs capitalize"
                title="Change role"
                aria-label="Change role"
              >
                {EVENT_ASSIGNMENT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
            {perms.canLead && (
              <button
                type="button"
                onClick={() => void handleComplete(assignment)}
                disabled={busy}
                className="btn-icon btn-icon-sm text-green-400"
                title="Mark assignment completed"
                aria-label="Mark assignment completed"
              >
                <CheckCircle className="w-3.5 h-3.5" />
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                onClick={() => void handleRemove(assignment)}
                disabled={busy}
                className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                title={isMine ? 'Remove your assignment' : 'Remove assignment'}
                aria-label={isMine ? 'Remove your assignment' : 'Remove assignment'}
              >
                {busy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const availableForAssign = roster.filter(
    (m) => m.status === 'active' && !activeAssignments.some((a) => a.member_id === m.id),
  );

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Assignees"
        icon={UserCheck}
        count={activeAssignments.length}
        className="mb-0 flex-wrap"
        actions={
          <>
          {targetType === 'event' &&
            perms.canCreate &&
            (myActiveAssignment ? (
              <button
                onClick={() => void handleSelfUnassign()}
                disabled={selfBusy}
                type="button"
                className="glass-button-secondary btn-xs text-red-300"
              >
                {selfBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
                Un-assign me
              </button>
            ) : (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowSelfRoleDropdown(!showSelfRoleDropdown)}
                  disabled={selfBusy}
                  className="glass-button-secondary btn-xs"
                  aria-expanded={showSelfRoleDropdown}
                >
                  {selfBusy ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  Self-assign
                  <ChevronDown className="w-3 h-3" />
                </button>
                {showSelfRoleDropdown && (
                  <div className="absolute top-full right-0 mt-1 glass-card overflow-hidden z-30 min-w-[140px] py-1">
                    {EVENT_ASSIGNMENT_ROLES.map((role) => (
                      <button
                        key={role}
                        onClick={() => void handleSelfAssign(role)}
                        className="w-full text-left px-3 py-1.5 text-sm text-glass-200 hover:bg-glass-200/10 transition-colors capitalize"
                      >
                        {role}
                      </button>
                    ))}
                    <button
                      onClick={() => void handleSelfAssign()}
                      className="w-full text-left px-3 py-1.5 text-sm text-glass-400 hover:bg-glass-200/10 transition-colors border-t border-glass-200/10"
                    >
                      No specific role
                    </button>
                  </div>
                )}
              </div>
            ))}
          {perms.canLead && (
            <button
              type="button"
              onClick={() => setShowAssignPicker(!showAssignPicker)}
              className="glass-button-sm"
              aria-expanded={showAssignPicker}
            >
              <UserPlus className="w-3.5 h-3.5" />
              Assign member
            </button>
          )}
          </>
        }
      />

      {notOnRoster && (
        <div className="alert alert-warning" role="status">
          <Info />
          <span className="flex-1">Not on the roster. Ask an event admin to add you.</span>
        </div>
      )}

      {showAssignPicker && perms.canLead && (
        <div className="subcard p-3 flex items-center gap-2 flex-wrap">
          <select
            value={assignMemberId}
            onChange={(e) => setAssignMemberId(e.target.value)}
            className="glass-select text-sm flex-1 min-w-[180px]"
            aria-label="Roster member"
          >
            <option value="">Select roster member…</option>
            {availableForAssign.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name} ({m.role})
              </option>
            ))}
          </select>
          <select
            value={assignRole}
            onChange={(e) => setAssignRole(e.target.value as EventAssignmentRole)}
            className="glass-select text-sm capitalize"
            aria-label="Assignment role"
          >
            {EVENT_ASSIGNMENT_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void handleLeadAssign()}
            disabled={assigning || !assignMemberId}
            className="glass-button-sm"
          >
            {assigning ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Assign
          </button>
        </div>
      )}

      {activeAssignments.length === 0 ? (
        <EmptyState bare compact icon={UserCheck} title="No staff assigned yet" description={targetType === 'event' ? 'Assign the people responsible for running this event. Participating players are managed under Participants.' : 'Assign the staff responsible for coordinating this project and its events.'} />
      ) : (
        <div className="space-y-2">{activeAssignments.map(renderAssignment)}</div>
      )}

      {pastAssignments.length > 0 && (
        <details className="group">
          <summary className="text-xs text-glass-500 cursor-pointer hover:text-glass-300 transition-colors">
            {pastAssignments.length} past assignment{pastAssignments.length === 1 ? '' : 's'} (completed/removed)
          </summary>
          <div className="space-y-2 mt-2">{pastAssignments.map(renderAssignment)}</div>
        </details>
      )}
    </div>
  );
};
